function getLegalLang(): 'en' | 'es' {
  const stored = localStorage.getItem('wb_lang');
  return stored === 'es' ? 'es' : 'en';
}

function PrivacyEN() {
  return <>
    <h1>Privacy Policy</h1>
    <p className="lp-legal-date">Last updated: May 2025</p>

    <h2>1. Data controller</h2>
    <p>The controller of personal data collected through this service is <strong>White Bot</strong>, reachable at <a href="mailto:whitebot.admin@gmail.com">whitebot.admin@gmail.com</a>.</p>

    <h2>2. Data we collect</h2>
    <p>We collect only the data necessary to provide the service:</p>
    <ul>
      <li><strong>Account data:</strong> email address and encrypted password (managed by Supabase Auth).</li>
      <li><strong>Usage data:</strong> bot-configured actions, technical execution logs, slot status.</li>
      <li><strong>Linking session data:</strong> session metadata (duration, status). We never store game credentials.</li>
      <li><strong>Billing data:</strong> subscription history and active plan. Payment data is managed entirely by Stripe and is not stored on our servers.</li>
      <li><strong>Technical data:</strong> IP address, user agent, access date and time, for security and diagnostic purposes.</li>
    </ul>

    <h2>3. Purpose and legal basis</h2>
    <ul>
      <li><strong>Contract performance (Art. 6.1.b GDPR):</strong> account management, service delivery, billing.</li>
      <li><strong>Legitimate interest (Art. 6.1.f GDPR):</strong> service security, fraud detection, technical improvement.</li>
      <li><strong>Consent (Art. 6.1.a GDPR):</strong> marketing communications, if the user expressly consents.</li>
    </ul>

    <h2>4. Data retention</h2>
    <p>Account and usage data are retained while the account is active and for <strong>30 days</strong> after cancellation or account deletion, unless a longer retention period is required by law. Technical logs are automatically deleted after 90 days.</p>

    <h2>5. Recipients and international transfers</h2>
    <p>We use <strong>Supabase</strong> as our database and authentication platform (data processor). Supabase may store data on servers outside the European Economic Area. In such cases, the transfer is covered by the Standard Contractual Clauses approved by the European Commission.</p>
    <p>We do not sell or share personal data with third parties for commercial purposes.</p>

    <h2>6. Your rights</h2>
    <p>You may exercise the following rights at any time:</p>
    <ul>
      <li><strong>Access:</strong> obtain confirmation of what data we process about you.</li>
      <li><strong>Rectification:</strong> correct inaccurate or incomplete data.</li>
      <li><strong>Erasure:</strong> request deletion of your data ("right to be forgotten").</li>
      <li><strong>Portability:</strong> receive your data in a structured, commonly used format.</li>
      <li><strong>Objection and restriction:</strong> object to processing or request its restriction in certain circumstances.</li>
    </ul>
    <p>To exercise any of these rights, send an email to <a href="mailto:whitebot.admin@gmail.com">whitebot.admin@gmail.com</a> with the subject "GDPR Rights Request". We will respond within 30 days.</p>
    <p>If you believe the processing does not comply with regulations, you may lodge a complaint with your national data protection authority (in the EU, the authority where you reside).</p>

    <h2>7. Security</h2>
    <p>We apply reasonable technical and organisational measures to protect your data: encryption in transit (HTTPS/TLS), restricted database access, two-factor authentication on administration systems, and periodic security reviews.</p>

    <h2>8. Cookies</h2>
    <p>We use only strictly necessary session cookies required for authentication. We do not use tracking, advertising, or third-party analytics cookies.</p>

    <h2>9. Changes to this policy</h2>
    <p>We may update this policy when necessary. If changes are substantial, we will notify registered users by email. The last updated date appears at the top of the document.</p>

    <h2>10. Contact</h2>
    <p>For any privacy enquiry: <a href="mailto:whitebot.admin@gmail.com">whitebot.admin@gmail.com</a></p>
  </>;
}

function PrivacyES() {
  return <>
    <h1>Política de Privacidad</h1>
    <p className="lp-legal-date">Última actualización: mayo de 2025</p>

    <h2>1. Responsable del tratamiento</h2>
    <p>El responsable del tratamiento de los datos personales recogidos a través de este servicio es <strong>White Bot</strong>, contactable en <a href="mailto:whitebot.admin@gmail.com">whitebot.admin@gmail.com</a>.</p>

    <h2>2. Datos que recopilamos</h2>
    <ul>
      <li><strong>Datos de cuenta:</strong> dirección de correo electrónico, contraseña cifrada (gestionada por Supabase Auth).</li>
      <li><strong>Datos de uso:</strong> acciones configuradas en el bot, logs técnicos de ejecución, estado del slot.</li>
      <li><strong>Datos de sesión de vinculación:</strong> metadatos de la sesión temporal (duración, estado). Nunca almacenamos credenciales del juego.</li>
      <li><strong>Datos de facturación:</strong> historial de suscripción y plan activo. Los datos de pago son gestionados íntegramente por Stripe y no se almacenan en nuestros servidores.</li>
      <li><strong>Datos técnicos:</strong> dirección IP, agente de usuario, fecha y hora de acceso, con fines de seguridad y diagnóstico.</li>
    </ul>

    <h2>3. Finalidad y base jurídica del tratamiento</h2>
    <ul>
      <li><strong>Ejecución del contrato (art. 6.1.b RGPD):</strong> gestión de la cuenta, prestación del servicio de automatización, facturación.</li>
      <li><strong>Interés legítimo (art. 6.1.f RGPD):</strong> seguridad del servicio, detección de fraudes, mejora técnica del sistema.</li>
      <li><strong>Consentimiento (art. 6.1.a RGPD):</strong> comunicaciones de marketing, si el usuario las acepta expresamente.</li>
    </ul>

    <h2>4. Conservación de los datos</h2>
    <p>Los datos de cuenta y uso se conservan mientras la cuenta esté activa y durante <strong>30 días</strong> tras la cancelación o eliminación de la cuenta. Los logs técnicos se eliminan automáticamente a los 90 días.</p>

    <h2>5. Destinatarios y transferencias internacionales</h2>
    <p>Utilizamos <strong>Supabase</strong> como plataforma de base de datos y autenticación (procesador de datos). Supabase puede almacenar datos en servidores situados fuera del Espacio Económico Europeo. En tal caso, la transferencia se ampara en las cláusulas contractuales tipo aprobadas por la Comisión Europea.</p>
    <p>No vendemos ni cedemos datos personales a terceros con fines comerciales.</p>

    <h2>6. Derechos del usuario</h2>
    <ul>
      <li><strong>Acceso, rectificación, supresión, portabilidad, oposición y limitación.</strong></li>
    </ul>
    <p>Para ejercerlos, envía un correo a <a href="mailto:whitebot.admin@gmail.com">whitebot.admin@gmail.com</a> con el asunto «Ejercicio de derechos RGPD». Responderemos en el plazo máximo de 30 días. Puedes presentar reclamación ante la <strong>AEPD</strong> en <a href="https://www.aepd.es" target="_blank" rel="noopener">www.aepd.es</a>.</p>

    <h2>7. Seguridad</h2>
    <p>Aplicamos medidas técnicas y organizativas razonables: cifrado en tránsito (HTTPS/TLS), acceso restringido a la base de datos, autenticación de dos factores en los sistemas de administración, y revisiones periódicas de seguridad.</p>

    <h2>8. Cookies</h2>
    <p>Utilizamos únicamente cookies de sesión estrictamente necesarias para el funcionamiento de la autenticación. No empleamos cookies de rastreo, publicidad ni analítica de terceros.</p>

    <h2>9. Cambios en esta política</h2>
    <p>Podemos actualizar esta política cuando sea necesario. Si los cambios son sustanciales, te notificaremos por correo electrónico.</p>

    <h2>10. Contacto</h2>
    <p>Para cualquier consulta sobre privacidad: <a href="mailto:whitebot.admin@gmail.com">whitebot.admin@gmail.com</a></p>
  </>;
}

export function PrivacyPage() {
  const lang = getLegalLang();
  const termsHref = lang === 'es' ? '/terminos' : '/terms';

  return (
    <div className="lp lp-legal-page">
      <nav className="lp-nav">
        <a href="/" className="lp-nav-logo"><img src="/logo.png" alt="White Bot" /></a>
        <div className="lp-nav-links">
          <a href="/">{ lang === 'es' ? 'Inicio' : 'Home' }</a>
          <a href="/dashboard" className="lp-btn-outline">{ lang === 'es' ? 'Entrar' : 'Log in' }</a>
        </div>
      </nav>
      <div className="lp-legal-shell">
        <div className="lp-legal-doc">
          {lang === 'es' ? <PrivacyES /> : <PrivacyEN />}
        </div>
      </div>
      <footer className="lp-footer">
        <div className="lp-container lp-footer-inner">
          <span className="lp-footer-logo">White Bot</span>
          <nav className="lp-footer-links">
            <a href="/">{ lang === 'es' ? 'Inicio' : 'Home' }</a>
            <a href={termsHref}>{ lang === 'es' ? 'Términos de uso' : 'Terms of use' }</a>
            <a href="mailto:whitebot.admin@gmail.com">{ lang === 'es' ? 'Contacto' : 'Contact' }</a>
          </nav>
          <p className="lp-footer-copy">© 2025 White Bot · Beta</p>
        </div>
      </footer>
    </div>
  );
}
